import { css } from 'lit';

/**
 * # Q1 View Styles
 * 
 * ## Anchor Links
 * 
 * The following CSS variables area available for `<a>` elements:
 * --link-color-default
 * --link-color-visited
 * --link-color-focus
 * --link-font-weight-default
 * --link-font-weight-focus
 * --link-text-decoration-default
 * --link-text-decoration-focus
 */
export const viewStyles = css`

  /*=== VIEW HOST ===*/

  :host {
    display: block;
    background: var(--gray-1);

    font-family: 'system-ui', sans-serif;
    line-height: 1.5em;

    /* VIEW VARIABLES */
    --q1-view-width: var(--q1-content-width-normal);
    --view-item-spacing: 1.5rem;
  }
  @media(min-width: 650px) {
    :host { --view-item-spacing: 2rem; }
  }
  @media(min-width: 1000px) {
    :host { --view-item-spacing: 2.5rem; }
  }

  /*=== UTILITY CLASSES ===*/

  .hide-until-bp0,
  .hide-until-bp1,
  .hide-until-bp2,
  .hide-until-bp3,
  .hide-until-bp4 {
    display: none;
  }
  @media(min-width: 400px) { .hide-until-bp0 { display: contents; } }
  @media(min-width: 500px) { .hide-until-bp1 { display: contents; } }
  @media(min-width: 650px) { .hide-until-bp2 { display: contents; } }
  @media(min-width: 1000px) { .hide-until-bp3 { display: contents; } }
  @media(min-width: 1500px) { .hide-until-bp4 { display: contents; } }

  .hide-at-bp0,
  .hide-at-bp1,
  .hide-at-bp2,
  .hide-at-bp3,
  .hide-at-bp4 {
    display: none;
  }
  @media(max-width: 399px) { .hide-at-bp0 { display: contents; } }
  @media(max-width: 499px) { .hide-at-bp1 { display: contents; } }
  @media(max-width: 649px) { .hide-at-bp2 { display: contents; } }
  @media(max-width: 999px) { .hide-at-bp3 { display: contents; } }
  @media(max-width: 1499px) { .hide-at-bp4 { display: contents; } }

  /*=== LAYOUT ===*/

  view-content {
    display: block;
    margin: 0 auto;
    padding: 8rem 2rem 4rem 2rem;
    
    box-sizing: content-box;
    max-width: var(--q1-view-width);
    min-height: 60vh;
  }

  view-content hr {
    border: 0;
    border-top: 1px solid var(--gray-4);
    margin: var(--view-item-spacing) 0;
    display: block;
  }

  view-content p.subtitle {
    margin: var(--view-item-spacing) 0;
    display: block;
    font-size: 1.1rem;
  }

  view-content {
    --subtitle-color: var(--gray-6);
  }
  view-content p.subtitle.hero {
    position: relative;
    font-style: italic;
    font-weight: 200;
    font-size: 1.3rem;
    line-height: 1.6em;
    padding-left: 2rem;
    
    color: var(--subtitle-color);
    --link-color-default: var(--subtitle-color);
    --link-color-visited: var(--subtitle-color);
    --link-color-focus: var(--subtitle-color);
  }
  view-content p.subtitle.hero::after {
    content: '';
    display: block;
    position: absolute;

    left: 0;
    bottom: 0;
    top: 0;
    width: 10px;

    background: var(--subtitle-color);
    opacity: 0.2;
  }

  view-content p.subtitle.hero.primary,
  view-content p.subtitle.hero.primary::after {
    --subtitle-color: var(--sl-color-primary-700);
  }
  view-content p.subtitle.hero.secondary,
  view-content p.subtitle.hero.secondary::after {
    --subtitle-color: var(--sl-color-warning-700);
  }
  view-content p.subtitle.hero.danger,
  view-content p.subtitle.hero.danger::after {
    --subtitle-color: var(--red-7);
  }
  view-content p.subtitle.hero.success,
  view-content p.subtitle.hero.success::after {
    --subtitle-color: var(--green-7);
  }

  view-content sl-alert {
    margin: var(--view-item-spacing) 0;
  }
  @media(min-width: 1000px) {
    view-content sl-alert {
      margin: 3.5rem 0;
    }
  }

  /*=== HEADER STYLES ===*/

  view-content h1,
  view-content h2,
  view-content h3 {
    line-height: 1.3em;
  }

  view-content h1 .prefix,
  view-content h2 .prefix,
  view-content h3 .prefix {
    display: block;
    font-size: var(--header-prefix-font-size, unset);
    font-weight: var(--header-prefix-font-weight, 100);
    line-height: var(--header-prefix-line-height, 1.3em);
  }

  header-bar {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    margin-bottom: var(--view-item-spacing);
    gap: 1rem;
    
    position: relative;
    overflow: hidden;

    --header-prefix-font-size: unset;
    --header-prefix-font-weight: 100;

    --header-icon-size: 14rem;
    --header-icon-color: var(--yellow-4);
    --header-icon-opacity: 0.4;
    --header-icon-bottom-position: -1rem;
    --header-icon-right-position: 1rem;
    --header-bar-padding-trim: 7rem; /* adjust if icon is clipped */
  }
  header-bar.with-border {
    border-bottom: 1px solid var(--gray-4);
  }
  header-bar.align-center {
    align-items: center;
  }
  header-bar:has(.header-icon) {
    padding-top: calc(
      var(--header-icon-size) + var(--header-icon-bottom-position) - var(--header-bar-padding-trim)
    );
  }

  sl-tab-panel header-bar {
    margin-top: 1rem;
  }

  header-bar.medium h1 {
    font-size: 1.8rem;

    --header-prefix-font-size: 1.3rem;
    --header-prefix-line-height: 1.5em;
  }
  header-bar.small h1 {
    font-size: 1.5rem;

    --header-prefix-font-size: 1.2rem;
    --header-prefix-line-height: 1.4em;
  }

  header-bar header-content {
    display: flex; 
    flex-direction: column;
    flex: 1 1 auto;
  }
  header-bar header-content.with-gap {
    gap: 1rem;
  }
  header-bar.medium header-content.with-gap {
    gap: 0.5rem;
  }
  header-bar.small header-content.with-gap {
    gap: 0.25rem;
  }

  header-bar header-content.with-gap h1,
  header-bar header-content.with-gap h2,
  header-bar header-content.with-gap h3 {
    margin: 0;
  }

  header-bar sl-avatar {
    --size: 5.5rem;
  }
  header-bar.medium sl-avatar {
    --size: 4.5rem;
  }
  header-bar.small sl-avatar {
    --size: 3.75rem;
  }

  header-bar h1,
  header-bar h2,
  header-bar h3 {
    margin-top: 0;
    z-index: 1;
    flex: 1 1 auto;
  }

  header-bar a.prefix {
    --link-text-decoration-default: none;
  }

  header-bar sl-breadcrumb-item::part(base) {
    font-size: 1rem;
  }
  header-bar sl-breadcrumb-item::part(label) {
    font-weight: 300;
  }

  header-bar sl-icon.header-icon {
    font-size: var(--header-icon-size);
    color: var(--header-icon-color);
    opacity: var(--header-icon-opacity);
    position: absolute;
    bottom: var(--header-icon-bottom-position);
    right: var(--header-icon-right-position);
    z-index: 0;
  }

  /*=== FORM CUSTOM ELEMENTS ===*/

  field-spacer {
    display: block;
  }
  field-pair {
    display: flex;
    flex-wrap: wrap;
    column-gap: 2rem;
    row-gap: 1.5rem;
    margin: 1.5rem 0;
  }
  field-pair input-wrapper,
  field-pair sl-input,
  field-pair sl-textarea,
  field-pair sl-select,
  field-pair sl-checkbox,
  field-pair sl-range,
  field-pair sl-radio-group,
  field-pair q1-tel-input,
  field-pair q1-places-input,
  field-pair q1-image-upload-input,
  field-pair q1-md-textarea,
  field-pair field-spacer {
    flex: 1 1 15rem;
    min-width: min(15rem, 100%);
  }

  fieldset-panel {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    padding: 1.5rem;
    margin: 1.5rem 0;
    border-radius: 7px;
    background: rgba(255, 255, 255, 0.3);
    border: 1px solid var(--gray-3);
  }

  /** IMPROVEME: This can be refactored with '.label-on-left' */
  radio-group-set {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;

    --radio-group-set-label-width: 10rem;
    --radio-group-set-gap: 0.5rem;
  }
  radio-group-set > sl-radio-group::part(form-control) {
    display: flex;
    align-items: center;
    gap: var(--radio-group-set-gap);
    flex-wrap: wrap;
  }
  radio-group-set > sl-radio-group::part(form-control-label) {
    width: var(--radio-group-set-label-width);
  }
  radio-group-set > sl-radio-group::part(form-control-help-text) {
    display: block;
  }
  @media (min-width: 500px) {
    radio-group-set > sl-radio-group::part(form-control-help-text) {
      margin-left: calc(var(--radio-group-set-label-width) + var(--radio-group-set-gap));
      flex: 1 1 100%;
    }
  }

  /** When a radio button is selected but disabled it, visual emphasize it. */
  sl-radio-button[disabled].selected::part(label) {
    font-weight: 700;
  }

  /*=== SIMPLE LIST ===*/

  simple-list {
    display: block;
    width: 100%;
    margin: 0;

    border: solid 1px var(--gray-2);
    border-radius: 12px;
    overflow: hidden;
  }

  simple-list .list-item {
    padding: 0.75rem 1rem;
    background: white;
    border: 1px solid var(--gray-1);
    margin: -1px; /* to prevent double borders */

    transition: all 0.2s ease-in-out;
  }
  simple-list .list-item:hover,
  simple-list .list-item:focus {
    background: var(--gray-1);
  }

  simple-list .empty-placeholder {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: var(--gray-6);
    font-size: 1rem;
    line-height: 1.5em;
    font-weight: 300;
    font-style: italic;
  }


  /*=== STANDARD OVERRIDES ===*/

  a {
    color: var(--link-color-default, var(--blue-7));
    text-decoration: var(--link-text-decoration-default, underline);
    font-weight: var(--link-font-weight-default, normal);
    transition: all 0.2s ease-in-out;
  }
  a:visited {
    color: var(--link-color-visited, var(--blue-8));
  }
  a:focus, a:hover {
    color: var(--link-color-focus, var(--blue-9));
    font-weight: var(--link-font-weight-focus, var(--link-font-weight-default, normal));
    text-decoration: var(--link-text-decoration-focus, underline);
  }

  /*=== TABLE STYLES ===*/

  table.q1 {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    border: none;

    /* Note: Borders are applied on cells not table */
    --table-border-radius: 7px;
    --table-border-width: 1px;
    --table-border-style: solid;
    --table-outer-border-color: var(--gray-3);
    --table-inner-border-color: var(--gray-2);

    --table-cell-padding: var(--sl-spacing-medium);
    
    --table-th-bg-color: var(--gray-3);
    --table-th-color: var(--gray-9);
    --table-th-font-size: var(--sl-font-size-medium);
    --table-th-font-weight: var(--sl-font-weight-semibold);
    
    --table-td-bg-color: var(--gray-0);
    --table-td-bg-stripe-color: var(--gray-1);
    --table-td-bg-focus-color: var(--yellow-0);
    --table-td-color: var(--gray-7);
    --table-td-font-size: var(--sl-font-size-medium);
    --table-td-font-weight: var(--sl-font-weight-normal);

    /* Internal Variables - Do not change */

    --table-outer-border:
      var(--table-border-width) var(--table-border-style) var(--table-outer-border-color);
    --table-inner-border:
      var(--table-border-width) var(--table-border-style) var(--table-inner-border-color);
  }

  table.q1 th:first-child { border-top-left-radius: var(--table-border-radius); }
  table.q1 th:last-child { border-top-right-radius: var(--table-border-radius); }
  table.q1 tr:last-child td:first-child { border-bottom-left-radius: var(--table-border-radius); }
  table.q1 tr:last-child td:last-child { border-bottom-right-radius: var(--table-border-radius); }

  table.q1 th:first-child,
  table.q1 td:first-child {
    border-left: var(--table-outer-border);
  }
  table.q1 th:last-child,
  table.q1 td:last-child {
    border-right: var(--table-outer-border);
  }

  table.q1 th {
    text-align: left;
    white-space: nowrap;
    
    padding: var(--table-cell-padding);
    border-top: var(--table-outer-border);
    border-bottom: var(--table-outer-border);

    background-color: var(--table-th-bg-color);
    color: var(--table-th-color);
    font-size: var(--table-th-font-size);
    font-weight: var(--table-th-font-weight);
  }

  table.q1 td {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    max-width: 15rem;

    padding: var(--table-cell-padding);
    border-bottom: var(--table-inner-border);

    background-color: var(--table-td-bg-color);
    color: var(--table-td-color);
    font-size: var(--table-td-font-size);
    font-weight: var(--table-td-font-weight);

    transition: all 0.2s ease-in-out;
  }
  table.q1.striped tbody tr:nth-child(even) td {
    background-color: var(--table-td-bg-stripe-color);
  }
  table.q1 tbody tr:focus td,
  table.q1 tbody tr:hover td {
    background-color: var(--table-td-bg-focus-color);
  }
  table.q1 tr:last-child td {
    border-bottom: var(--table-outer-border);
  }
  table.q1 td.full-width {
    max-width: none;
  }

  table.q1 td.actions {
    padding: var(--sl-spacing-x-small);
  }
  table.q1 td.actions sl-icon-button {
    font-size: 1.3rem;
  }

  /*=== FORM STYLES ===*/

  form.q1 h2 {
    margin: var(--view-item-spacing) 0;
  }

  form.q1 sl-input,
  form.q1 sl-textarea {
    line-height: 1.1rem;
  }
  form.q1 > input-wrapper:not(:last-child),
  form.q1 > radio-group-set:not(:last-child),
  form.q1 > sl-input:not(:last-child),
  form.q1 > sl-textarea:not(:last-child),
  form.q1 > sl-select:not(:last-child),
  form.q1 > sl-checkbox:not(:last-child),
  form.q1 > sl-range:not(:last-child),
  form.q1 > sl-radio-group:not(:last-child),
  form.q1 > q1-tel-input:not(:last-child),
  form.q1 > q1-places-input:not(:last-child),
  form.q1 > q1-image-upload-input:not(:last-child)
  form.q1 > q1-md-textarea:not(:last-child)
  form.q1 > field-spacer:not(:last-child) {
    margin-bottom: 1.5rem;
  }

  form.q1 label,
  form.q1 sl-input::part(form-control-label) {
    display: block;
    margin-bottom: 0.5rem;
  }
  form.q1 input-wrapper,
  form.q1 radio-group-set,
  form.q1 sl-input,
  form.q1 sl-textarea,
  form.q1 sl-select,
  form.q1 sl-checkbox,
  form.q1 sl-range,
  form.q1 sl-radio-group,
  form.q1 q1-tel-input,
  form.q1 q1-places-input,
  form.q1 q1-image-upload-input,
  form.q1 q1-md-textarea {
    --sl-spacing-3x-small: 0.5rem;
  }

  form.q1 sl-alert {
    margin: 2rem 0;
  }

  /*=== VIEW LAYOUT STYLES ====*/

  column-wrapper {
    display: flex;
    flex-direction: column;
    gap: var(--view-item-spacing);
    margin: var(--view-item-spacing) 0;

    --left-ratio: 1;
    --right-ratio: 1;
  }
  column-wrapper.left-weighted {
    --left-ratio: 7;
    --right-ratio: 3;
  }
  column-wrapper.right-weighted {
    --left-ratio: 3;
    --right-ratio: 7;
  }
  @media (min-width: 650px) {
    column-wrapper {
      flex-direction: row;
    }
  }

  column-wrapper > left-column,
  column-wrapper > right-column {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    min-width: 15rem;
  }
  column-wrapper > left-column {
    flex: var(--left-ratio) 0 0;
  }
  column-wrapper > right-column {
    flex: var(--right-ratio) 0 0;
  }

  column-wrapper.left-weighted > left-column {
    order: 2;
  }
  column-wrapper.left-weighted > right-column {
    order: 1;
  }
  @media (min-width: 650px) {
    column-wrapper.left-weighted > left-column {
      order: 1;
    }
    column-wrapper.left-weighted > right-column {
      order: 2;
    }
  }

  /*=== REUSABLE Q1 CUSTOM CLASSES & CSS ELEMENTS ====*/

  empty-alert {
    display: flex;
    align-items: center;
    gap: 1rem;
    
    color: var(--gray-6);
    font-weight: 300;
    font-style: italic;
    
    padding: 1.5rem;
    background: var(--gray-2);
    border-radius: 7px;
    border: 1px dashed var(--gray-4);
  }
  empty-alert sl-icon {
    font-size: 1.7rem;
  }

  notice-panel {
    display: flex;
    align-items: center;
    justify-content: center;

    --np-border-color: var(--gray-4);
    --np-background: var(--gray-0);
    --np-color: var(--gray-7);
    --np-icon-color: var(--gray-5);
    --np-icon-font-size: 5rem;
    --np-h2-font-size: 1.5rem;
    --np-box-shadow: var(--sl-shadow-medium);
    --np-max-width: 23rem;
  }
  notice-panel.hero {
    --np-max-width: 35rem;
  }
  notice-panel .inner {
    padding: 1.5rem;
    text-align: center;
    border-radius: 12px;
    max-width: var(--np-max-width);

    border: 1px solid var(--np-border-color);
    background: var(--np-background);
    box-shadow: var(--np-box-shadow);
    color: var(--np-color);
  }
  notice-panel .inner sl-icon {
    font-size: var(--np-icon-font-size);
    color: var(--np-icon-color);
  }
  notice-panel.hero .inner sl-icon {
    --np-icon-font-size: 7rem;
  }
  notice-panel .inner h2 {
    margin: 1rem 0;
    font-size: var(--np-h2-font-size);
    line-height: 1.5em;
  }
  notice-panel .inner sl-button {
    margin: 1rem 0;
  }

  notice-panel.primary {
    --np-background: var(--sl-color-primary-50);
    --np-border-color: var(--sl-color-primary-300);
    --np-icon-color: var(--sl-color-primary-600);
  }
  notice-panel.secondary {
    --np-background: var(--sl-color-warning-50);
    --np-border-color: var(--sl-color-warning-300);
    --np-icon-color: var(--sl-color-warning-600);
  }
  notice-panel.danger {
    --np-background: var(--red-0);
    --np-border-color: var(--red-3);
    --np-icon-color: var(--red-6);
  }
  notice-panel.success {
    --np-background: var(--green-0);
    --np-border-color: var(--green-3);
    --np-icon-color: var(--green-6);
  }

  notice-bar {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.5rem;
    margin: 0 0 var(--view-item-spacing) 0;
    padding: 0.5rem 1rem;
    border-radius: 3px;
    box-shadow: var(--sl-shadow-medium);
    background: var(--nb-background);
    color: var(--nb-color);

    flex-wrap: wrap;

    --nb-background: var(--gray-7);
    --nb-color: white;
  }
  @media(min-width: 650px) {
    notice-bar {
      flex-wrap: nowrap;
    }
  }

  notice-bar .content {
    flex: 1 1 auto;
  }
  notice-bar sl-button::part(label) {
    color: var(--nb-color);
    font-weight: 600;
  }
  notice-bar sl-icon {
    color: var(--nb-color);
  }

  notice-bar.primary {
    --nb-background: var(--sl-color-primary-300);
    --nb-color: var(--sl-color-primary-900);
  }
  notice-bar.secondary {
    --nb-background: var(--sl-color-warning-200);
    --nb-color: var(--sl-color-warning-900);
  }
  notice-bar.danger {
    --nb-background: var(--red-9);
    --nb-color: white;
  }
  notice-bar.success {
    --nb-background: var(--green-3);
    --nb-color: var(--green-9);
  }

  button-bar {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
  }
  button-bar.right-align { /* IMPROVEME: Should rename this right-justify */
    justify-content: flex-end;
  }
  button-bar.left-align { /* IMPROVEME: Should rename this right-justify */
    justify-content: flex-start;
  }
  button-bar.align-center {
    align-items: center;
  }

  filter-bar {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
  }
  filter-bar.right-align {
    justify-content: flex-end;
  }
  filter-bar.left-align {
    justify-content: flex-start;
  }

  input-wrapper {
    display: block;
  }
  input-wrapper > .help-text {
    display: block;
    color: var(--sl-input-help-text-color);
    margin-top: var(--sl-spacing-3x-small);
    font-size: var(--sl-input-help-text-font-size-medium);
  }

  ul.icon-list {
    list-style: none;
    display: flex;
    flex-wrap: wrap;
    gap: 2rem;
    padding: 0;
    margin: var(--view-item-spacing) 0;
  }
  ul.icon-list li {
    display: flex;
    gap: 1rem;
    align-items: flex-start;

    flex: 1 0 80%;
  }
  ul.icon-list li icon-frame {
    flex: 0 0 auto;
  }
  ul.icon-list li span {
    display: block;
    flex: 1 1 auto;
    font-size: 1rem;
    line-height: 1.3em;
    font-weight: 100;
  }
  ul.icon-list li span strong {
    font-size: 1.1rem;
    line-height: 1.5em;
    font-weight: 600;
  }
  @media(min-width: 650px) {
    ul.icon-list li {
      max-width: calc(50% - 1rem);
      flex-basis: 18rem;
    }
  }

  icon-frame {
    display: flex;
    position: relative;
    width: 4rem;
    height: 4rem;
    align-items: center;
    justify-content: center;
    margin: 0.5rem 0;

    border: 1px solid var(--icon-frame-border-color);
    border-radius: 100%;

    --icon-frame-border-color: var(--sl-color-primary-500);
    --icon-frame-background: var(--sl-color-primary-300);
  }
  icon-frame.secondary {
    --icon-frame-border-color: var(--sl-color-warning-500);
    --icon-frame-background: var(--sl-color-warning-300);
  }
  icon-frame.danger {
    --icon-frame-border-color: var(--red-5);
    --icon-frame-background: var(--red-3);
  }
  icon-frame.success {
    --icon-frame-border-color: var(--green-5);
    --icon-frame-background: var(--green-3);
  }

  icon-frame::before {
    content: '';
    position: absolute;
    background: var(--icon-frame-background);
    border-radius: 100%;
    opacity: 0.3;
    
    width: 4rem; height: 4rem;
    top: 0; left: 0;
    z-index: 2;
  }
  icon-frame.with-shadow::after {
    content: '';
    position: absolute;
    background: var(--icon-frame-background);
    border-radius: 100%;
    opacity: 0.1;
    
    width: 4rem; height: 4rem;
    bottom: -4px; right: -5px;
    z-index: 1;
  }
  icon-frame sl-icon {
    font-size: 2rem;
    display: block;
    color: var(--icon-frame-border-color);
    z-index: 3;
  }

  /*=== SHOELACE / INPUT FOCUSED UTILITY CLASSES ====*/

  .label-on-left {
    --label-width: 3.75rem;
    --gap-width: 1rem;
  }

  @media(min-width: 650px) {
    .label-on-left::part(form-control) {
      display: grid;
      grid: auto / var(--label-width) 1fr;
      gap: var(--sl-spacing-3x-small) var(--gap-width);
      align-items: center;
    }
  
    .label-on-left::part(form-control-label) {
      text-align: left;
    }
  
    .label-on-left::part(form-control-help-text) {
      grid-column-start: 2;
    }
  }

  /*=== AVATAR STACK ====*/

  avatar-stack {
    display: flex;
    flex-direction: row;

    --avatar-border-color: white;
    --avatar-stack-size: 1.2rem;
  }
  avatar-stack sl-avatar {
    --size: var(--avatar-stack-size);
  }
  avatar-stack sl-avatar:not(:first-child) {
    margin-left: -0.55rem;
  }
  avatar-stack sl-avatar::part(base) {
    border: 1px solid var(--avatar-border-color);
  }

  /*=== SHOELACE OVERRIDES ====*/

  sl-tag sl-icon {
    margin-right: 0.5rem;
  }

  sl-divider {
    --color: var(--gray-3);
    margin: var(--view-item-spacing) 0;
  }

  sl-alert::base {
    border-radius: 12px;
  }
  sl-alert[variant='primary']::part(base) {
    border: 1px solid var(--yellow-3);
    background: var(--yellow-0);
  }
  sl-alert[variant='warning']::part(base) {
    border: 1px solid var(--teal-2);
    background: var(--teal-0);
  }
  sl-alert[variant='danger']::part(base) {
    border: 1px solid var(--red-3);
    background: var(--red-0);
  }
  sl-alert[variant='success']::part(base) {
    border: 1px solid var(--green-3);
    background: var(--green-0);
  }
  sl-alert[variant='neutral']::part(base) {
    border: 1px solid var(--gray-3);
    background: var(--gray-0);
  }

  sl-button[variant=primary]::part(base) {
    /* commenting this out for now to improve theming */
    /* color: var(--gray-9); */
  }
  
  sl-button.secondary::part(base) {
    background: var(--teal-6);
  }

  sl-button.elevated::part(base) {
    transition: box-shadow 0.3s ease-in-out;
    box-shadow: var(--sl-shadow-medium);
  }
  sl-button.elevated:hover::part(base),
  sl-button.elevated:focus::part(base) {
    box-shadow: var(--sl-shadow-x-large);
  }

  sl-button.jumbo::part(base) {
    padding: 1rem 1.5rem;
  }
  sl-button.jumbo::part(label) {
    font-size: 1.3rem;
    font-weight: 600;
    line-height: 1.6em;
  }

  /** Styles a spinner used as a button prefix / suffix */
  sl-button sl-spinner {
    --indicator-color: white;
    --track-width: 3px;
  }

  sl-select .inner-label {
    white-space: nowrap; /* prevent prefix label from wrapping */
  }

  sl-option sl-avatar {
    --size: 1.5rem;
  }

  sl-radio-group.wrap-button-group::part(button-group__base) {
    flex-wrap: wrap;
    row-gap: 0.5rem;
  }

  sl-radio-group sl-radio {
    margin: 0.5rem 0;
  }
  sl-radio small {
    color: var(--gray-6);
    font-weight: 200;
    font-style: italic;
  }

  sl-tab {
    overflow: hidden;
  }

  sl-dialog::part(panel) {
    overflow: hidden;
  }

  sl-dialog button-bar {
    justify-content: flex-end;
  }
  sl-dialog small {
    font-style: italic;
    color: var(--gray-6);
    font-weight: 200;
    line-height: 1em;
  }

  /*=== SHOELACE COLOR THEMES ====*/

  /*
    To use these, just add the class to a parent element and all of the shoelace
    components will inherit the color theme.
  */

  /** This makes the teal color the primary shoelace color */
  .sl-secondary {
    --sl-color-primary-50: var(--teal-0);
    --sl-color-primary-100: var(--teal-1);
    --sl-color-primary-200: var(--teal-2);
    --sl-color-primary-300: var(--teal-3);
    --sl-color-primary-400: var(--teal-4);
    --sl-color-primary-500: var(--teal-5);
    --sl-color-primary-600: var(--teal-6);
    --sl-color-primary-700: var(--teal-7);
    --sl-color-primary-800: var(--teal-8);
    --sl-color-primary-900: var(--teal-9);
    --sl-color-primary-950: var(--teal-9);
  }
  
  /** This inherits shoelace primary from theme color vars & warning from theme secondary vars */
  .sl-themed {
    --sl-color-primary-50: var(--theme-color-primary-0);
    --sl-color-primary-100: var(--theme-color-primary-1);
    --sl-color-primary-200: var(--theme-color-primary-2);
    --sl-color-primary-300: var(--theme-color-primary-3);
    --sl-color-primary-400: var(--theme-color-primary-4);
    --sl-color-primary-500: var(--theme-color-primary-5);
    --sl-color-primary-600: var(--theme-color-primary-6);
    --sl-color-primary-700: var(--theme-color-primary-7);
    --sl-color-primary-800: var(--theme-color-primary-8);
    --sl-color-primary-900: var(--theme-color-primary-9);
    --sl-color-primary-950: var(--theme-color-primary-9);
    
    --sl-color-warning-50: var(--theme-color-secondary-0);
    --sl-color-warning-100: var(--theme-color-secondary-1);
    --sl-color-warning-200: var(--theme-color-secondary-2);
    --sl-color-warning-300: var(--theme-color-secondary-3);
    --sl-color-warning-400: var(--theme-color-secondary-4);
    --sl-color-warning-500: var(--theme-color-secondary-5);
    --sl-color-warning-600: var(--theme-color-secondary-6);
    --sl-color-warning-700: var(--theme-color-secondary-7);
    --sl-color-warning-800: var(--theme-color-secondary-8);
    --sl-color-warning-900: var(--theme-color-secondary-9);
    --sl-color-warning-950: var(--theme-color-secondary-9);
  }

  /** This inherits shoelace primary colors from theme secondary color vars */
  .sl-themed-secondary {
    --sl-color-primary-50: var(--theme-color-secondary-0);
    --sl-color-primary-100: var(--theme-color-secondary-1);
    --sl-color-primary-200: var(--theme-color-secondary-2);
    --sl-color-primary-300: var(--theme-color-secondary-3);
    --sl-color-primary-400: var(--theme-color-secondary-4);
    --sl-color-primary-500: var(--theme-color-secondary-5);
    --sl-color-primary-600: var(--theme-color-secondary-6);
    --sl-color-primary-700: var(--theme-color-secondary-7);
    --sl-color-primary-800: var(--theme-color-secondary-8);
    --sl-color-primary-900: var(--theme-color-secondary-9);
    --sl-color-primary-950: var(--theme-color-secondary-9);
  }

`;
